import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, doc } from 'firebase/firestore';
import { ArrowLeft, Send, MessageCircle, X, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import MessageBubble, { DateDivider } from './MessageBubble';
import { useSupportMessages } from './useSupportMessages';
import { useImageUpload } from './useImageUpload';
import { db } from '../../firebase/firebaseConfig';

function SupportChat({ onClose }) {
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const {
    messages,
    loading,
    error,
    sending,
    accountUUID,
    sendMessage,
    markAsRead,
    refreshMessages,
    clearError
  } = useSupportMessages();

  const {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    uploadImages,
    cancelUploads,
    hasImages
  } = useImageUpload();

  const busy = sending || uploading;

  // Cancel uploads on unmount
  useEffect(() => {
    return () => cancelUploads();
  }, [cancelUploads]);

  // Mark messages as read when they change
  useEffect(() => {
    if (messages.length > 0) {
      markAsRead();
    }
  }, [messages, markAsRead]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !hasImages) || busy) return;

    const messageToSend = newMessage;
    setNewMessage('');
    clearError();

    let uploadedImages = [];
    if (hasImages) {
      setUploading(true);
      try {
        // Pre-generate message ID for storage path
        const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
        const newMessageRef = doc(messagesRef);
        const messageId = newMessageRef.id;

        uploadedImages = await uploadImages(accountUUID, messageId);
        clearImages();
        setUploading(false);
        await sendMessage(messageToSend, uploadedImages, messageId);
      } catch (err) {
        console.error('Error uploading images:', err);
        setUploading(false);
        // Don't clear images on failure so user can retry
        return;
      }
    } else {
      await sendMessage(messageToSend);
    }

    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      addImages(files);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      addImages(files);
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach((message) => {
      const messageDate = message.createdAt instanceof Date
        ? message.createdAt.toDateString()
        : new Date(message.createdAt).toDateString();

      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({ type: 'date', date: message.createdAt });
      }
      groups.push({ type: 'message', message });
    });

    return groups;
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col"
      style={{ zIndex: 9999 }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop zone overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-slate-800/20 border-2 border-dashed border-slate-400 rounded-lg z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-xl px-6 py-4 shadow-lg">
            <p className="text-slate-700 font-medium">Drop images here</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shadow-sm safe-area-top">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="flex-shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-slate-900">Feedback & Support</h1>
          <p className="text-xs text-slate-500 truncate">We're here to help and listen</p>
        </div>
        <MessageCircle className="h-5 w-5 text-slate-400 flex-shrink-0" />
      </div>

      {/* Welcome Banner */}
      <div className="flex-shrink-0 bg-slate-800 text-white px-4 py-3">
        <p className="text-sm leading-relaxed">
          <span className="font-medium">We love hearing from you!</span> feedback, questions, or ideas. Tell us everything, and we'll get back ASAP.
        </p>
      </div>

      {/* Error Banner (dismissible) */}
      {error && (
        <div className="flex-shrink-0 bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between">
          <p className="text-red-800 text-sm">{error}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshMessages}
              className="text-red-700 hover:text-red-800 hover:bg-red-100"
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={clearError}
              className="h-6 w-6 text-red-700 hover:text-red-800 hover:bg-red-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 min-h-0"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800 mx-auto mb-2"></div>
              <p className="text-sm text-slate-500">Loading messages...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-xs">
              <MessageCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium mb-1">Start a conversation</p>
              <p className="text-sm text-slate-500">
                Send us a message below and we'll respond as soon as we can.
              </p>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            {groupMessagesByDate(messages).map((item, index) => (
              item.type === 'date' ? (
                <DateDivider key={`date-${index}`} date={item.date} />
              ) : (
                <MessageBubble
                  key={item.message.id}
                  message={item.message}
                  isOwn={item.message.senderType === 'user'}
                />
              )
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Image Preview Strip */}
      {pendingImages.length > 0 && (
        <div className="flex-shrink-0 bg-white border-t border-slate-200 px-2 pb-2">
          <div className="max-w-2xl mx-auto flex gap-2 overflow-x-auto pt-3 px-1">
            {pendingImages.map((img) => (
              <div key={img.id} className="relative flex-shrink-0 w-16 h-16">
                <img
                  src={img.previewUrl}
                  alt="Pending"
                  className="w-16 h-16 rounded-lg object-cover border border-slate-200"
                />
                {img.status === 'uploading' && (
                  <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{img.progress}%</span>
                  </div>
                )}
                {img.status === 'error' && (
                  <div className="absolute inset-0 bg-red-500/40 rounded-lg flex items-center justify-center">
                    <X className="h-4 w-4 text-white" />
                  </div>
                )}
                {img.status !== 'uploading' && (
                  <button
                    className="absolute -top-1.5 -right-1.5 bg-slate-800 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors"
                    onClick={() => removeImage(img.id)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area - with safe area padding for mobile */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 p-2 safe-area-bottom">
        <div className="max-w-2xl mx-auto flex gap-2 items-center">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            multiple
            onChange={(e) => {
              if (e.target.files.length > 0) {
                addImages(Array.from(e.target.files));
              }
              e.target.value = '';
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="flex-shrink-0 h-10 w-10 text-slate-500 hover:text-slate-700"
          >
            <Paperclip className="h-5 w-5" />
          </Button>
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Type your message..."
              disabled={sending}
              rows={1}
              className="w-full resize-none rounded-2xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent disabled:opacity-50 disabled:bg-slate-50"
              style={{
                fontSize: '16px',
                lineHeight: '1.25',
                minHeight: '40px',
                maxHeight: '120px'
              }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
            />
          </div>
          <Button
            onClick={handleSend}
            disabled={(!newMessage.trim() && !hasImages) || busy}
            className="flex-shrink-0 rounded-full h-10 w-10 p-0 bg-slate-800 hover:bg-slate-700"
          >
            {busy ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-slate-400 text-center mt-1.5 hidden sm:block">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>,
    document.body
  );
}

export default SupportChat;
